import { useRef, useState } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import { EmployeeRootPageDocument, EmployeeRouteMetaDocument } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { observer } from 'mobx-react'

import type { Back, Tab } from 'modules/core/components/Card/types'
import { useSection } from 'modules/core/components/SectionContainer'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import { normalizeAndFormatPhoneNumber } from 'modules/core/lib/phoneNumber'

import { BecomeModal } from 'modules/authentication/public'
import {
  ActivityEventsSidepanel,
  RESOURCE_TYPE,
} from 'modules/core/components/ActivityLog/ActivityEventsSidepanel'
import { useGetOnboardingProcess } from 'modules/employee_onboarding/hooks/useGetOnboardingProcess'
import ActivateModal from 'modules/employees/components/Actions/ActivateModal'
import DisableMfaModal from 'modules/employees/components/Actions/DisableMfaModal'
import SetWelcomeSpaceAccessModal from 'modules/employees/components/Actions/SetWelcomeSpaceAccessModal'
import UninviteModal from 'modules/employees/components/Actions/UninviteModal'
import RegularAccessBanner from 'modules/employees/components/blocks/RegularAccessBanner'
import TerminationBanner from 'modules/employees/components/blocks/TerminationBanner'
import { RouteContextParams } from 'modules/employees/lib/RouteContextParams'
import { RouteContext } from 'modules/employees/lib/types'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { timeoffModuleEnabledInAvailabilities } from 'modules/timeoff/lib/timeoffModuleEnabled'

const EmployeeRootPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { loadingModuleAvailabilities, moduleAvailabilities, hasPerformanceFullVisiblity } =
    useOutletContext<RouteContextParams>()

  const [openBecome, setOpenBecome] = useState(false)
  const [openUninvite, setUninvite] = useState(false)
  const [openDisableMfa, setOpenDisableMfa] = useState(false)
  const [openSetWelcomeSpaceAccess, setWelcomeSpaceAccess] = useState(false)
  const [openActivate, setActivate] = useState(false)
  const [openActivitySidepanel, setOpenActivitySidepanel] = useState(false)
  const { id } = useStrictParams<'id'>()
  const { employee: sessionOwner, company } = useCurrentSession()
  const cardEmployeeRight = useRef<HTMLElement | null>(null)
  const headerTitle = useRef<HTMLElement | null>(null)
  const section = useSection()
  // NOTE: sections of level 3 or more have their own layout so we can hide this one
  const hasOwnLayout = section.level >= 3
  const {
    loading: loadingEmployeeRootPageQuery,
    data,
    client,
  } = useQuery(EmployeeRootPageDocument, {
    variables: { id: String(id), companyId: company.id },
    skip: !id,
  })

  const currentEmployee = data?.employees.employee
  const canTerminateEmployee = data?.employees.employee?._policies?.terminateEmployees.can ?? false

  const isManagedByMe = data?.employees.managed.nodes.some((e) => e.id === String(id))
  const isMyProfile = sessionOwner?.id === String(id)

  const canSeePersonalInfo = currentEmployee?._policies?.readPersonalInfo.can ?? false

  const features = keyBy(data?.features?.featuresConnection?.nodes, 'name')
  const hasEmployeeOnboarding = features['employee_onboarding']?.enabled ?? false
  const seeOldCostCentersAssignments =
    features['see_old_cost_centers_assignments']?.enabled ?? false

  const canEditPersonalInfo = currentEmployee?.access.personalInfo?._policies?.edit.can ?? false
  const canSeePermissions = data?.apiCore._policies?.companyManagePermissions.can ?? false
  const employeeHasContract = !!currentEmployee?.referenceContract
  const permissionGroup = currentEmployee?.access.permissionGroup
  const permissionGroupName = permissionGroup?.name || ''
  const permissionGroupId = permissionGroup?.id?.toString() || ''
  const canSeePerformance = currentEmployee?._policies?.seeEmployeePerformanceTab.can ?? false
  const canSeeDocuments = currentEmployee?._policies?.seeDocuments.can ?? false
  const canSeeFutureEmployees = data?.employees._policies?.seeFutureEmployees.can ?? false
  const canSeeEmployeeProfile = data?.apiCore._policies?.companySeeEmployeeProfile.can ?? false
  const alwaysShowCustomTable = features['always_show_custom_table']?.enabled ?? false
  const canSeeCustomTables = features['employees_custom_tables']?.enabled && alwaysShowCustomTable
  const canSeeTrainingsCustomTable = features['trainings_custom_table']?.enabled ?? false
  const canSeeCompetenciesCustomTable = features['competencies_custom_table']?.enabled ?? false
  const canSeeGrowthPlansCustomTable = features['growth_plans_custom_table']?.enabled ?? false
  const isActivityLogEnabled = features['dev_activity_log']?.enabled ?? false

  const {
    loading: loadingOnboardingProcess,
    process: onboardingProcess,
    fetchOnboardingProcess,
  } = useGetOnboardingProcess()

  useEffect(() => {
    if (!currentEmployee?.id) return
    fetchOnboardingProcess({
      variables: {
        employeeId: currentEmployee.id,
      },
    })
  }, [currentEmployee?.id])

  const hasCompetencies = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.competencies
  )
  const inFreeTrial = currentEmployee?.access.company?.subscription?.inFreeTrial ?? false

  const getCanSeeTimeOffTab = (moduleAv: ModuleAvailabilityType[]) => {
    const timeoffEnabled = timeoffModuleEnabledInAvailabilities(moduleAv)

    return timeoffEnabled && (currentEmployee?._policies?.readLeaves.can ?? false)
  }

  const commonTabs = (moduleAv: ModuleAvailabilityType[]) => {
    let tabs: Tab[] = []

    const canSeeTimeOffTab = getCanSeeTimeOffTab(moduleAv)
    const canSeePlanningVersions = currentEmployee?._policies?.seePlanningVersions.can ?? false
    const canSeeTasksTab = isMyProfile || !!currentEmployee?._policies?.manageTasks.can
    const canSeeAssessments =
      currentEmployee?._policies?.seeEmployeeCompetenciesAssessments?.can ?? false
    const canSeeTrainings = currentEmployee?._policies?.seeEmployeeTrainings.can ?? false
    const canSeeOthersTab =
      canSeeCustomTables ||
      canSeeTrainingsCustomTable ||
      canSeeCompetenciesCustomTable ||
      canSeeGrowthPlansCustomTable

    if (canSeePlanningVersions) {
      tabs.push({
        name: i18n.t('time_planning.title'),
        href: navigation.employees.id.planningVersions.fullPath({ id }),
      })
    }

    if (canSeeDocuments) {
      tabs = tabs.concat([
        {
          name: i18n.t('admin_employee.documents'),
          href: navigation.employees.id.documents.list.fullPath({ id }),
        },
      ])
    }

    if (canSeeTimeOffTab) {
      tabs.push({
        name: i18n.t('time_off.title'),
        href: navigation.employees.id.timeOff.fullPath({ id }),
      })
    }

    if (canSeePerformance) {
      tabs.push({
        name: i18n.t('sidebar.performance_new'),
        href: navigation.employees.id.performance.fullPath({ id }),
      })
    }

    if (canSeeTasksTab) {
      tabs.push({
        name: i18n.t('tasks.header'),
        href: navigation.employees.id.tasks.fullPath({ id }),
      })
    }

    if (hasCompetencies && canSeeAssessments) {
      tabs.push({
        name: i18n.t('competencies_v2.title'),
        href: navigation.employees.id.competencies.fullPath({ id }),
      })
    }

    if (canSeeTrainings) {
      tabs.push({
        name: i18n.t('trainings.my_training.all_courses_table.training'),
        href: navigation.employees.id.training.fullPath({
          id: String(id),
        }),
      })
    }

    if (canSeeOthersTab) {
      tabs.push({
        name: i18n.t('custom_tables.header'),
        href: navigation.employees.id.customTables.fullPath({ id }),
      })
    }

    return tabs
  }

  const computeTabs = (moduleAv: ModuleAvailabilityType[]): Tab[] => {
    let tabs: Tab[] = []

    if (!currentEmployee) {
      return []
    }

    const canSeeContract = currentEmployee?._policies?.seeContract.can ?? false
    const canEditContract = currentEmployee?._policies?.editContract.can ?? false
    const canSeeContractsTab = canSeeContract && (canEditContract || employeeHasContract)

    const canSeeAttendance = currentEmployee?._policies?.seeAttendance.can ?? false

    const canSeeTimeOffTab = getCanSeeTimeOffTab(moduleAv)

    const canSeeTasksTab = isMyProfile || !!currentEmployee?._policies?.manageTasks.can

    const canSeeTimePlanningTab = currentEmployee?._policies?.seePlanningVersions.can ?? false

    const overviewEnabledByFeatures =
      [
        canSeeContractsTab,
        canSeeTimeOffTab,
        canSeeTasksTab,
        canSeeTimePlanningTab,
        canSeeAttendance,
      ].filter(Boolean).length >= 2

    const canSeeOverview = isManagedByMe || isMyProfile || overviewEnabledByFeatures

    if (canSeeOverview) {
      tabs.push({
        index: true,
        name: i18n.t('overview.title'),
        href: navigation.employees.id.fullPath({ id }),
      })
    }

    if (canSeeEmployeeProfile) {
      tabs = tabs.concat([
        {
          index: !canSeeOverview,
          name: i18n.t('profile.work_details'),
          href: !canSeeOverview
            ? navigation.employees.id.fullPath({ id })
            : navigation.employees.id.workDetails.fullPath({ id }),
        },
      ])
    }

    if (canSeePersonalInfo) {
      tabs = tabs.concat([
        {
          name: i18n.t('profile.personal_details'),
          href: navigation.employees.id.personal.fullPath({ id }),
        },
      ])
    }

    if (canSeeContractsTab) {
      tabs = tabs.concat([
        {
          name: i18n.t('admin_employee.contracts'),
          href: navigation.employees.id.contractVersions.fullPath({ id }),
        },
      ])
    }

    return [...tabs, ...commonTabs(moduleAvailabilities)]
  }

  const isLoading =
    loadingModuleAvailabilities || loadingOnboardingProcess || loadingEmployeeRootPageQuery

  const back: Back = {
    href: navigation.employees.list.fullPath(),
    label: i18n.t('employees.title'),
  }

  const tabs = computeTabs(moduleAvailabilities)

  const canSeeOverview = tabs.find((tab) => tab.name === i18n.t('overview.title')) !== undefined

  const context: RouteContext = {
    back,
    canSeePersonalInfo,
    canEditPersonalInfo,
    cardEmployeeRight,
    employeeId: currentEmployee?.id,
    hasPerformanceFullVisiblity,
    headerTitle,
    inFreeTrial,
    loading: isLoading,
    loadingModuleAvailabilities,
    moduleAvailabilities,
    loadingOnboardingProcess,
    onboardingProcess,
    showEmployeeSelector: true,
    tabs,
    canSeePermissions,
    permissionGroupName,
    permissionGroupId,
    canSeePerformance,
    seeOldCostCentersAssignments,
    canSeeOverview,
    canTerminateEmployee,
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      loading={isLoading}
      error={!currentEmployee}
      showHeader={false}
      onActionClick={(key) => {
        if (key === 'view-activity') {
          setOpenActivitySidepanel(true)
        } else if (key === 'whatsapp-chat') {
          const phoneNumberFormatted = normalizeAndFormatPhoneNumber({
            phoneNumber: currentEmployee?.phoneNumber,
            country: currentEmployee?.country,
          })
          const hasWhatsappUrl = phoneNumberFormatted !== null
          const whatsappUrl = hasWhatsappUrl ? `https://wa.me/${phoneNumberFormatted}` : ''
          track(trackingEvents.comms.openWhatsappChatWithEmployee)
          window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
        } else if (key === 'revoke-invite') {
          setUninvite(true)
        } else if (key === 'unterminate') {
          setActivate(true)
        } else if (key === 'become') {
          setOpenBecome(true)
        } else if (key === 'disable-mfa') {
          setOpenDisableMfa(true)
        } else if (key === 'terminate') {
          if (!currentEmployee?.id) return

          track(trackingEvents.employee.termination.terminateStart, {
            company_id: currentEmployee.legalEntity?.company?.id,
            user_id: currentEmployee.id,
            time_stamp: Date.now(),
          })
          navigate(navigation.employees.id.terminate.fullPath({ id: currentEmployee.id }))
        } else if (key === 'move-to-welcome-space') {
          setWelcomeSpaceAccess(true)
        }
      }}
    >
      <Box width="full" overflowY="hidden">
        {!hasOwnLayout && (
          <Box width="full">
            {currentEmployee && (
              <>
                <TerminationBanner
                  employee={currentEmployee}
                  canTerminateEmployee={canTerminateEmployee}
                />
                <RegularAccessBanner
                  employee={currentEmployee}
                  canSeeFutureEmployees={canSeeFutureEmployees}
                />
              </>
            )}
          </Box>
        )}
        <Box width="full" overflowY="auto">
          <Outlet context={context} />
        </Box>
      </Box>
      {openBecome && currentEmployee && (
        <BecomeModal
          accessId={currentEmployee.access.id}
          firstName={currentEmployee.firstName}
          onClose={() => setOpenBecome(false)}
        />
      )}
      {openUninvite && currentEmployee && (
        <UninviteModal
          accessId={currentEmployee.access.id}
          onClose={() => {
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
            setUninvite(false)
          }}
        />
      )}
      {openDisableMfa && currentEmployee && (
        <DisableMfaModal
          accessId={currentEmployee.access.id}
          onClose={() => {
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
            setOpenDisableMfa(false)
          }}
        />
      )}
      {openSetWelcomeSpaceAccess && currentEmployee && (
        <SetWelcomeSpaceAccessModal
          employeeId={currentEmployee.id}
          onClose={() => {
            setWelcomeSpaceAccess(false)
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
          }}
        />
      )}
      {currentEmployee && openActivate && (
        <ActivateModal
          employeeData={currentEmployee}
          hasEmployeeOnboarding={hasEmployeeOnboarding}
          onClose={() => {
            setActivate(false)
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
          }}
        />
      )}
      {currentEmployee && openActivitySidepanel && isActivityLogEnabled && (
        <ActivityEventsSidepanel
          resourceId={String(currentEmployee.id)}
          resourceType={RESOURCE_TYPE.Employee}
          resourceName={currentEmployee.firstName}
          onClose={() => setOpenActivitySidepanel(false)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default observer(EmployeeRootPage)
